import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './PromoTrialPopup.css';
import appIcon from '../../assets/logo_white_bg.png'; // Placeholder icon, replace with actual app icon
const PromoTrialPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const timeoutRef = useRef(null);
  const showDelayRef = useRef(null);
  const hasBeenShownRef = useRef(false); // Track if popup has been shown in this session

  // Check if popup has been permanently dismissed (forever)
  const isPopupDisabled = () => {
    return localStorage.getItem('360coordinates_popup_disabled') === 'true';
  };

  // Check if popup has been shown in this session
  const hasBeenShownInSession = () => {
    return sessionStorage.getItem('360coordinates_popup_shown') === 'true';
  };

  // Mark popup as shown in this session
  const markAsShownInSession = () => {
    sessionStorage.setItem('360coordinates_popup_shown', 'true');
  };

  // Disable popup forever
  const disablePopupForever = () => {
    localStorage.setItem('360coordinates_popup_disabled', 'true');
  };

  // Check if popup should be shown on current route
  const shouldShowOnRoute = () => {
    const path = location.pathname.toLowerCase();
    
    // Do NOT show on login/register pages
    if (path === '/login' || path === '/register') {
      return false;
    }
    
    // Do NOT show on dashboard routes
    if (path.startsWith('/buyer') || path.startsWith('/seller') || path.startsWith('/agent')) {
      return false;
    }
    
    // Do NOT show on viewdetails pages
    if (path.includes('/details/') || path.includes('/viewdetails')) {
      return false;
    }
    
    // Show on all other landing pages
    return true;
  };

  // Manual close handler - just closes without disabling
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 250); // Match CSS animation duration
    
    // Clear timeout if still running
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Handle CTA button click - opens Play Store and disables popup forever
  const handleCTAClick = () => {
    // Open Play Store link for 360Coordinates app
    window.open('https://play.google.com/store/apps/details?id=com.indiapropertys.app360', '_blank');
    
    // Disable popup forever
    disablePopupForever();
    
    // Close the popup
    handleClose();
  };

  useEffect(() => {
    // Don't show if popup is permanently disabled
    if (isPopupDisabled()) {
      return;
    }

    // Don't show if popup has already been shown in this session
    if (hasBeenShownInSession()) {
      return;
    }

    // Check if should show on current route
    if (!shouldShowOnRoute()) {
      return;
    }

    // Clear any existing timeouts
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Small delay to ensure smooth animation on page load
    showDelayRef.current = setTimeout(() => {
      setIsAnimating(true);
      setIsVisible(true);
      
      // Mark as shown in this session so it won't appear again
      markAsShownInSession();
      
      // Popup will remain visible until user clicks download or close button
    }, 300); // Small delay for smooth page load

    // Cleanup on unmount or route change
    return () => {
      if (showDelayRef.current) {
        clearTimeout(showDelayRef.current);
        showDelayRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname]); // Re-check when route changes

  // Don't render if not visible or popup is disabled
  if (!isVisible || isPopupDisabled()) {
    return null;
  }

  return (
    <div className={`promo-trial-popup-overlay ${isAnimating ? 'promo-trial-popup-visible' : 'promo-trial-popup-hidden'}`}>
      <div className="promo-trial-popup-container">
        <button 
          className="promo-trial-popup-close"
          onClick={handleClose}
          aria-label="Close popup"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div className="promo-trial-popup-content">
          <img 
  style={{
    height: "100px", 
    width: "100px", 
    borderRadius: "20px", 
    border: "0.5px solid #000000",
    // boxShadow: "1px 1px 1px black"
  }} 
  src={appIcon} 
  alt="360Coordinates App" 
/>
          <h2 className="promo-trial-popup-title">Download 360Coordinates App</h2>
          
          
          <ul className="promo-trial-popup-features">
  <li className="promo-trial-popup-feature">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
    <span>List & manage properties with exact location</span>
  </li>

  <li className="promo-trial-popup-feature">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
    <span>Pinpoint coordinates for accurate property mapping</span>
  </li>

  <li className="promo-trial-popup-feature">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
    <span>Share property location instantly with buyers</span>
  </li>

  <li className="promo-trial-popup-feature">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
    <span>Boost visibility for rent, sale & resale listings</span>
  </li>

  <li className="promo-trial-popup-feature">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
    <span>Easy property posting with smart tools</span>
  </li>
</ul>

          <button 
            className="promo-trial-popup-cta"
            onClick={handleCTAClick}
          >
            <svg 
              className="playstore-icon" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.609-.92V2.734a.996.996 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-7.134 4.115L7.88 16.42l6.619-5.713zm.805-.707L8.469 5.584l2.114-1.219 7.134 4.115-2.302 2.302zM21.5 11.5c.5.3.5.7 0 1l-3.25 1.875L15.75 12l2.5-2.375 3.25 1.875z"/>
            </svg>
            Download on Play Store
          </button>

          <p className="promo-trial-popup-limited">Free download </p>
        </div>
      </div>
    </div>
  );
};

export default PromoTrialPopup;